import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { tasksApi, type HumanTask } from "../api/tasks";
import { copyText } from "../lib/clipboard";
import { Modal } from "./Modal";
import { useToast } from "./toast/context";

interface Props {
  task: HumanTask | null;
  onClose: () => void;
}

/**
 * Outcome modal for agent-assigned human tasks: the operator marks the task
 * Done (optional feedback for the proposing agent) or Declines it (reason
 * required). Takes the queue shape (`HumanTask`); a raw `/api/v1/tasks` row
 * converts through `humanTaskOf`.
 */
export function TaskOutcomeModal({ task, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setNote("");
    setCopied(false);
  }, [task?.task_id]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ["tasks"] });
      onClose();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  async function markDone() {
    if (!task) return;
    const feedback = note.trim();
    await run(() => tasksApi.complete(task.task_id, feedback ? { feedback } : {}));
  }

  async function decline() {
    if (!task) return;
    const reason = note.trim();
    if (!reason) return;
    await run(() => tasksApi.dismiss(task.task_id, { reason }));
  }

  async function copyDraft(body: string) {
    if (!(await copyText(body))) {
      toast.error("Could not copy – select the text and copy it by hand.");
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const link = task?.linkedin_url ?? task?.action_url;

  return (
    <Modal
      open={!!task}
      onClose={onClose}
      eyebrow="Task"
      title={task?.title || task?.task_type || "Task"}
      footer={
        task ? (
          <>
            <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              className="btn btn-second btn-sm"
              onClick={decline}
              disabled={busy || !note.trim()}
            >
              Decline
            </button>
            <button className="btn btn-main btn-sm" onClick={markDone} disabled={busy}>
              Done
            </button>
          </>
        ) : null
      }
    >
      {!task ? null : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            {task.agent_slug ? (
              <span className="pill pill--signal">from {task.agent_slug}</span>
            ) : null}
            {link ? (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-ink-medium link-quiet"
              >
                Open {task.linkedin_url ? "LinkedIn" : "link"} ↗
              </a>
            ) : null}
            {task.message_url ? (
              <a
                href={task.message_url}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-ink-medium link-quiet"
              >
                Message ↗
              </a>
            ) : null}
          </div>

          {task.description ? (
            <section>
              <div className="label">Why</div>
              <p className="text-sm text-ink-medium leading-relaxed">{task.description}</p>
            </section>
          ) : null}

          {task.draft_message ? (
            <section>
              <div className="flex items-baseline justify-between">
                <div className="label mb-0">Draft</div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => copyDraft(task.draft_message as string)}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <pre className="input font-mono text-xs leading-relaxed whitespace-pre-wrap max-h-52 overflow-y-auto mt-1">
                {task.draft_message}
              </pre>
            </section>
          ) : null}

          <section>
            <div className="label">Feedback</div>
            <textarea
              className="input h-20"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional for Done — required as the reason to Decline."
            />
          </section>
        </div>
      )}
    </Modal>
  );
}
